import { useState, useEffect } from 'react';

/**
 * Custom hook for managing dark mode state.
 * Persists preference to localStorage and applies class to document root.
 */
export const useDarkMode = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize from localStorage
    try {
      const stored = localStorage.getItem('darkMode');
      if (stored !== null) {
        return JSON.parse(stored);
      }
    } catch {}
    // Default to false (light mode)
    return false;
  });

  // Apply changes and broadcast when local state updates
  useEffect(() => {
    try {
      // Persist to localStorage
      localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
    } catch {}

    // Apply to document for Tailwind
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Broadcast to all listeners in the app (same tab)
    try {
      window.dispatchEvent(new CustomEvent('darkModeUpdated', { detail: isDarkMode }));
    } catch {}
  }, [isDarkMode]);

  // Listen for updates coming from other components or tabs
  useEffect(() => {
    const handleDarkModeEvent = (e) => {
      const next = typeof e.detail === 'boolean' ? e.detail : isDarkMode;
      if (next !== isDarkMode) {
        setIsDarkMode(next);
      }
    };

    const handleStorage = (e) => {
      if (e.key && e.key !== 'darkMode') return;
      try {
        const stored = localStorage.getItem('darkMode');
        if (stored !== null) {
          const next = JSON.parse(stored);
          if (typeof next === 'boolean' && next !== isDarkMode) {
            setIsDarkMode(next);
          }
        }
      } catch {}
    };

    window.addEventListener('darkModeUpdated', handleDarkModeEvent);
    window.addEventListener('storage', handleStorage);
    return () => {
      window.removeEventListener('darkModeUpdated', handleDarkModeEvent);
      window.removeEventListener('storage', handleStorage);
    };
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode((prev) => !prev);
  };

  const setDarkMode = (value) => {
    if (typeof value === 'boolean') setIsDarkMode(value);
  };

  return { isDarkMode, toggleDarkMode, setDarkMode };
};
